import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { MaskedLine } from "./Reveal";
import { scrollToId } from "../lib/scroll";

export const Hero = () => {
  const sectionRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 18 });
  const sy = useSpring(my, { stiffness: 60, damping: 18 });
  const rotateX = useTransform(sy, [-0.5, 0.5], [7, -7]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-7, 7]);
  const qx = useTransform(sx, [-0.5, 0.5], [-26, 26]);
  const qy = useTransform(sy, [-0.5, 0.5], [-18, 18]);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const onMouseMove = (e) => {
    const r = sectionRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={sectionRef}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="grid-lines relative flex min-h-screen flex-col justify-between overflow-hidden pt-28 md:pt-32"
      style={{ perspective: 1200 }}
    >
      <motion.div
        aria-hidden="true"
        style={{ rotateX, rotateY, x: qx, y: qy }}
        className="pointer-events-none absolute -right-[8vw] top-[6vh] select-none text-[44vw] font-black leading-[0.8] tracking-tighter text-black/[0.05] md:-right-[4vw] md:text-[34vw]"
      >
        Q
      </motion.div>

      <motion.div style={{ y: parallaxY, opacity: fade }} className="relative mx-auto w-full max-w-[1440px] px-5 md:px-10">
        <div className="mb-8 flex items-center gap-4 text-[11px] font-bold uppercase tracking-[0.2em] text-black/60 md:text-[13px]">
          <span className="inline-block h-2 w-2 bg-black" />
          <span data-testid="hero-eyebrow">Organizational intelligence for confident decisions</span>
        </div>

        <h1 className="max-w-[1100px] text-[44px] font-extrabold leading-[1.02] tracking-tighter sm:text-6xl md:text-[72px]">
          <MaskedLine delay={0.1}>Every organization is</MaskedLine>
          <MaskedLine delay={0.22}>already an intelligence.</MaskedLine>
          <MaskedLine delay={0.34} innerClassName="text-black/40">
            It's just an extremely slow one.
          </MaskedLine>
        </h1>

        <div className="mt-10 grid gap-10 md:grid-cols-12 md:items-end">
          <div className="md:col-span-6">
            <MaskedLine delay={0.5}>
              <p data-testid="hero-subcopy" className="max-w-xl text-base leading-relaxed text-black/70 md:text-lg">
                Your company's truth is scattered across repos, deals, calendars and inboxes — each
                system telling the truth, collectively wrong. Qwiri holds the full working state,
                reasons across all of it at once, and tells you what is true. With citations.
              </p>
            </MaskedLine>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-book-demo-btn"
                onClick={() => scrollToId("demo")}
                className="hard-shadow border-2 border-black bg-black px-8 py-4 text-[12px] font-bold uppercase tracking-[0.2em] text-white transition-colors duration-150 hover:bg-white hover:text-black"
              >
                Book a demo
              </button>
              <button
                data-testid="hero-see-problem-btn"
                onClick={() => scrollToId("problem")}
                className="border-2 border-black bg-white px-8 py-4 text-[12px] font-bold uppercase tracking-[0.2em] text-black transition-colors duration-150 hover:bg-black hover:text-white"
              >
                See the problem
              </button>
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="hidden md:col-span-6 md:block"
          >
            <div className="ml-auto w-fit border-2 border-black bg-white p-5 hard-shadow">
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/50">Live from the drift feed</div>
              <div className="mt-2 text-sm font-bold">Commitment gap — Wells Fargo</div>
              <div className="mt-1 text-xs text-black/60">Proposal sent · repo idle 11 days · demo Thursday</div>
              <div className="mt-3 flex gap-2">
                <span className="border border-black px-2 py-1 text-[10px] font-bold uppercase tracking-widest">Confirm</span>
                <span className="border border-black/30 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-black/50">Dismiss</span>
                <span className="bg-black px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-white">Open in Ask</span>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.1 }}
        className="relative mt-16 border-t border-black/15"
      >
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 divide-y divide-black/15 px-5 text-[11px] font-bold uppercase tracking-[0.2em] text-black/60 sm:grid-cols-3 sm:divide-x sm:divide-y-0 md:px-10 md:text-[12px]">
          <div className="py-4 pr-4" data-testid="hero-meta-1">L1 → L3 in ninety days</div>
          <div className="py-4 sm:px-4" data-testid="hero-meta-2">Every answer cited, or silence</div>
          <div className="py-4 sm:pl-4" data-testid="hero-meta-3">Your permissions, inherited — never widened</div>
        </div>
      </motion.div>
    </section>
  );
};
